import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EditSvg } from "../../../../assets/Icons/edit (1).svg";
import { ReactComponent as DeselectSvg } from "../../../../assets/Icons/Deselect icon.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAlert } from "../../../../hooks/useAlert";
import {
  useGetAllUsersQuery,
  useGetUsersByCompanyQuery,
} from "../../../../redux-toolkit/features/company/companyApiSlice";
import {
  useDeleteEmployeeMutation,
  useToggleEmployeeAccessMutation,
} from "../../../../redux-toolkit/features/employee/employeeApiSlice";
import ProgressBar from "../../../UI/ProgressBar";
import "../../../UI/Tables/TableUi.css";
import sortUp from "../../../../assets/employee/sort_up.svg";
import sortDown from "../../../../assets/employee/sort_down.svg";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Modal,
  Pagination,
  TextField,
  Tooltip,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Toggle } from "../../../UI/Toggle";
import emptyStateSearch from "../../../../assets/employee/empty_state_search.svg";
import "../../../UI/Delete Modal/DeleteModal.css";
import "./Employee.css";

const DELETION_NOTE_MAX_LENGTH = 500;

const confirmModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  maxHeight: "80vh",
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "40px 20px",
};

export const SortIcon = ({ sortOrder }) => {
  return (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: "2px",
        width: "fit-content",
      }}
    >
      <img src={sortUp} alt="" />
      <img src={sortDown} alt="" />
    </span>
  );
};

export const EmployeeTable = ({
  refetchData,
  onEditClick,
  searchQuery = "",
  setSelectedEmployee,
  selectedEmployee,
  isAdmin,
  companyId,
  isActiveFilter,
}) => {
  const { displayAlert } = useAlert();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState("asc");
  const role = useSelector((state) => state.auth.user.role);
  const currentUserId = useSelector((state) => state.auth.user?._id);
  const allowedAccess =
    useSelector((state) => state.auth.user?.allowedAccess) || [];
  const canManageEmployeeActions =
    role === "SUPERADMIN" ||
    role === "ADMIN" ||
    (role === "CUSTOM_ROLE" &&
      (allowedAccess.includes("SUPERADMIN_EMPLOYEES") ||
        allowedAccess.includes("ADMIN_EMPLOYEES")));
  const canDeleteEmployee = canManageEmployeeActions;
  const canViewEmployeeActivity = Boolean(isAdmin) || canManageEmployeeActions;
  const isProtectedEmployee = (employee) => employee?.isMutable === false;
  const protectedEmployeeTooltip = "Protected account";
  const useCompanyQuery = Boolean(isAdmin && companyId);

  const handleViewActivity = (employeeId) => {
    if (!employeeId) return;
    if (isAdmin && companyId) {
      navigate(`/admin/manage-company/employee/${companyId}/${employeeId}/activity`);
      return;
    }
    navigate(`/super-admin/manage-organization/employee/${employeeId}/activity`);
  };

  const {
    data: allUsersData,
    refetch: refetchAllUsers,
    isLoading: isAllUsersLoading,
    isFetching: isAllUsersFetching,
  } = useGetAllUsersQuery(
    {
      sortBy: sortBy,
      type: sortType,
      query: searchQuery,
      limit: 10,
      page: page,
      ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: useCompanyQuery,
    },
  );

  const {
    data: companyUsersData,
    refetch: refetchCompanyUsers,
    isLoading: isCompanyUsersLoading,
    isFetching: isCompanyUsersFetching,
  } = useGetUsersByCompanyQuery(
    {
      companyId,
      page,
      limit: 10,
      query: searchQuery,
      ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !useCompanyQuery,
    },
  );

  const users = useCompanyQuery ? companyUsersData : allUsersData;
  const isLoading = useCompanyQuery
    ? isCompanyUsersLoading || isCompanyUsersFetching
    : isAllUsersLoading || isAllUsersFetching;

  const [toggleEmployeeAccess] = useToggleEmployeeAccessMutation();
  const [deleteEmployee, { isLoading: isDeleting }] =
    useDeleteEmployeeMutation();
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletionNote, setDeletionNote] = useState("");

  const handleChange = async (id) => {
    await toggleEmployeeAccess(id)
      .unwrap()
      .then((res) => {
        displayAlert({
          open: true,
          message: res?.message || `Employee status changed`,
          type: res?.data?.isActive ? "success" : "error",
          timeout: "1500",
        });
      })
      .catch((err) => {
        displayAlert({
          open: true,
          message:
            err?.data?.message ||
            `Error while updating the employee activity status`,
          type: "error",
          timeout: "1500",
        });
      });
    if (useCompanyQuery) {
      refetchCompanyUsers();
    } else {
      refetchAllUsers();
    }
  };

  const openStatusConfirm = (employee) => {
    if (isProtectedEmployee(employee)) {
      displayAlert({
        open: true,
        message: protectedEmployeeTooltip,
        type: "error",
        timeout: "2000",
      });
      return;
    }
    setStatusConfirm({
      id: employee._id,
      name: `${employee?.fname || ""} ${employee?.lname || ""}`.trim() || "this employee",
      isActive: Boolean(employee?.isActive),
    });
  };

  const closeStatusConfirm = () => setStatusConfirm(null);

  const confirmStatusChange = async () => {
    if (!statusConfirm?.id) return;
    const employeeId = statusConfirm.id;
    closeStatusConfirm();
    await handleChange(employeeId);
  };

  const openDeleteConfirm = (employee) => {
    if (!canDeleteEmployee) {
      displayAlert({
        open: true,
        message: "You do not have permission to delete employees",
        type: "error",
        timeout: "2000",
      });
      return;
    }
    if (isProtectedEmployee(employee)) {
      displayAlert({
        open: true,
        message: protectedEmployeeTooltip,
        type: "error",
        timeout: "2000",
      });
      return;
    }
    if (String(employee?._id) === String(currentUserId)) {
      displayAlert({
        open: true,
        message: "You cannot delete your own account",
        type: "error",
        timeout: "2000",
      });
      return;
    }
    setDeletionNote("");
    setDeleteConfirm({
      id: employee._id,
      name:
        `${employee?.fname || ""} ${employee?.lname || ""}`.trim() ||
        "this employee",
    });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm(null);
    setDeletionNote("");
  };

  const confirmDeleteEmployee = async () => {
    if (!deleteConfirm?.id || isDeleting) return;
    try {
      const res = await deleteEmployee({
        id: deleteConfirm.id,
        note: deletionNote.trim(),
      }).unwrap();
      displayAlert({
        open: true,
        message: res?.message || "Employee deleted successfully",
        type: "success",
        timeout: "2000",
      });
      setSelectedEmployee((prev) =>
        prev.filter((id) => String(id) !== String(deleteConfirm.id))
      );
      closeDeleteConfirm();
      if (useCompanyQuery) {
        refetchCompanyUsers();
      } else {
        refetchAllUsers();
      }
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || "Error while deleting the employee",
        type: "error",
        timeout: "2000",
      });
    }
  };

  const selectHandler = (e, _id, employee) => {
    if (String(_id) === String(currentUserId) || isProtectedEmployee(employee)) {
      return;
    }
    if (e.target.checked) {
      setSelectedEmployee([...selectedEmployee, _id]);
    } else {
      setSelectedEmployee(selectedEmployee.filter((item) => item !== _id));
    }
  };

  const handleSorting = (sortKey) => {
    setSortBy(sortKey);
    setSortType(!sortType || sortType === "desc" ? "asc" : "desc");
  };

  const handleCopyEmail = async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      displayAlert({
        open: true,
        message: "Email copied",
        type: "success",
        timeout: "1500",
      });
    } catch (err) {
      displayAlert({
        open: true,
        message: "Failed to copy email",
        type: "error",
        timeout: "1500",
      });
    }
  };

  useEffect(() => {
    if (refetchData) {
      if (useCompanyQuery) {
        refetchCompanyUsers();
      } else {
        refetchAllUsers();
      }
    }
  }, [refetchData, useCompanyQuery, refetchCompanyUsers, refetchAllUsers]);

  useEffect(() => {
    setPage(1);
  }, [isActiveFilter, searchQuery, companyId]);

  return (
    <div className="employee__table_wrap">
      {isLoading ? (
        <div className="employee__loader">
          <ProgressBar />
        </div>
      ) : (
        <TableContainer
          className="employee__table_container"
          sx={{
            padding: "0 1px",
            maxWidth: "100%",
            overflow: "auto",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            className="employee__table"
          >
            <TableHead>
              <TableRow
                sx={{ borderRadius: "15px 15px 0px 0px" }}
                className="tableUi__head"
              >
                {canManageEmployeeActions && (
                  <TableCell
                    className="employeeTable__stickyCol employeeTable__stickyCol--head employeeTable__col--select"
                    sx={{
                      borderRadius: "15px 0px 0px 0px",
                    }}
                    align="center"
                  >
                    <div className="employeeTable__selectHead">
                      {selectedEmployee.length > 0 && (
                        <DeselectSvg
                          style={{ width: "18px", cursor: "pointer" }}
                          onClick={() => {
                            setSelectedEmployee([]);
                          }}
                        />
                      )}
                      Select
                    </div>
                  </TableCell>
                )}

                <TableCell
                  className="employeeTable__col--name"
                  align="center"
                  onClick={() => handleSorting("fname")}
                >
                  <div className="tableUi__head_sortable">
                    First Name
                    {/* <SortIcon sortOrder={sortType} /> */}
                  </div>
                </TableCell>
                <TableCell className="employeeTable__col--name" align="center">
                  Last Name
                </TableCell>
                <TableCell className="employeeTable__col--email" align="center">
                  Email Id
                </TableCell>
                <TableCell
                  className="employeeTable__col--company"
                  align="center"
                  onClick={() => handleSorting("company_name")}
                >
                  <div className="tableUi__head_sortable">
                    Company
                    {/* <SortIcon sortOrder={sortType} /> */}
                  </div>
                </TableCell>
                <TableCell className="employeeTable__col--empId" align="center">
                  Emp Id
                </TableCell>
                <TableCell
                  className="employeeTable__col--status"
                  sx={{
                    borderRadius: !canViewEmployeeActivity
                      ? "0px 15px 0px 0px"
                      : "",
                  }}
                  align="center"
                >
                  Status
                </TableCell>

                {canViewEmployeeActivity && (
                  <TableCell
                    className="employeeTable__col--action"
                    sx={{ borderRadius: "0px 15px 0px 0px" }}
                    align="center"
                  >
                    Action
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {searchQuery &&
              (!users?.data?.users?.list ||
                users?.data?.users?.list.length === 0) ? (
                <TableRow>
                  <TableCell
                    colSpan={canViewEmployeeActivity ? 7 : isAdmin ? 6 : 7}
                    align="center"
                    sx={{
                      border: "none",
                      padding: "60px 20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "20px",
                      }}
                    >
                      <img
                        src={emptyStateSearch}
                        alt="No search results"
                        style={{
                          width: "220px",
                          height: "165px",
                          paddingTop: "40px",
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                users &&
                users?.data?.users?.list.map((row) => {
                  const isProtected = isProtectedEmployee(row);
                  return (
                    <TableRow
                      className={`tableUi___body${
                        selectedEmployee?.includes(row?._id)
                          ? " employeeTable__row--selected"
                          : ""
                      }`}
                      key={row?._id}
                    >
                      {canManageEmployeeActions && (
                        <TableCell
                          className={`employeeTable__stickyCol employeeTable__stickyCol--body employeeTable__col--select${
                            selectedEmployee?.includes(row?._id)
                              ? " employeeTable__stickyCol--selected"
                              : ""
                          }`}
                          sx={{
                            padding: "0px !important",
                          }}
                          align="center"
                        >
                          <Checkbox
                            size="small"
                            checked={selectedEmployee?.includes(row?._id)}
                            value={row?._id}
                            disabled={
                              String(row?._id) === String(currentUserId) ||
                              isProtected
                            }
                            sx={{
                              p: "4px",
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                            onChange={(e) => selectHandler(e, row?._id, row)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="employeeTable__col--name" align="center">
                        <div className="tableUi__rankscir">
                          <div className="tableUi__rank"> {row?.fname}</div>
                        </div>
                      </TableCell>
                      <TableCell className="employeeTable__col--name" align="center">
                        {row?.lname}
                      </TableCell>
                      <TableCell className="employeeTable__col--email" align="center">
                        <div className="employeeTable__emailCell">
                          <span className="employeeTable__emailText" title={row?.email}>
                            {row?.email}
                          </span>
                          <ContentCopyOutlinedIcon
                            className="employeeTable__emailCopy"
                            titleAccess="Copy email"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyEmail(row?.email);
                            }}
                            fontSize="small"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="employeeTable__col--company" align="center">
                        {row?.company?.name}
                      </TableCell>
                      <TableCell className="employeeTable__col--empId" align="center">
                        {row?.employeeId}
                      </TableCell>
                      <TableCell
                        className="employeeTable__col--status"
                        align="center"
                      >
                        <div className="employeeTable__statusCell">
                          {canManageEmployeeActions && (
                            <Tooltip
                              title={isProtected ? protectedEmployeeTooltip : ""}
                            >
                              <span>
                                <Toggle
                                  isOn={row?.isActive}
                                  handleToggle={() => {
                                    openStatusConfirm(row);
                                  }}
                                  id={row?._id}
                                  inputProps={{
                                    style: {
                                      position: "absolute",
                                    },
                                    disabled: isProtected,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          )}
                          {!canManageEmployeeActions &&
                            (row?.isActive ? "Active" : "Inactive")}
                        </div>
                      </TableCell>
                      {canViewEmployeeActivity && (
                        <TableCell
                          className="employeeTable__col--action"
                          align="center"
                        >
                          <div className="employeeTable__actionButtons">
                            <Tooltip title="View activity">
                              <VisibilityOutlinedIcon
                                onClick={() => handleViewActivity(row?._id)}
                                style={{ fontSize: "18px", cursor: "pointer" }}
                              />
                            </Tooltip>
                            {canManageEmployeeActions ? (
                              <Tooltip
                                title={
                                  isProtected
                                    ? protectedEmployeeTooltip
                                    : "Edit employee"
                                }
                              >
                                <span style={{ display: "inline-flex" }}>
                                  <EditSvg
                                    onClick={() => {
                                      if (isProtected) return;
                                      onEditClick && onEditClick(row?._id);
                                    }}
                                    style={{
                                      fontSize: "15px",
                                      cursor: isProtected
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: isProtected ? 0.4 : 1,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            ) : null}
                            {canDeleteEmployee && !isProtected ? (
                              <Tooltip title="Delete employee">
                                <DeleteOutlineIcon
                                  onClick={() => openDeleteConfirm(row)}
                                  style={{
                                    fontSize: "18px",
                                    cursor: "pointer",
                                    color: "#d32f2f",
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {searchQuery &&
      (!users?.data?.users?.list ||
        users?.data?.users?.list.length === 0) ? null : (
        <div className="employee__pagination_cont">
          <Pagination
            count={users?.data?.users?.totalPages}
            shape="rounded"
            page={page}
            onChange={(e, newPage) => {
              setPage(newPage);
            }}
          />
        </div>
      )}

      <Modal
        open={Boolean(statusConfirm)}
        onClose={closeStatusConfirm}
        aria-labelledby="employee-status-confirm-title"
        aria-describedby="employee-status-confirm-description"
      >
        <Box sx={confirmModalStyle}>
          <div className="deletemodal">
            <h3
              id="employee-status-confirm-title"
              className="ftwt__r deletemodal__title"
            >
              Are you sure you want to{" "}
              {statusConfirm?.isActive ? "disable" : "enable"}{" "}
              {statusConfirm?.name}?
            </h3>
            <div className="deletemodal__action">
              <Button
                onClick={closeStatusConfirm}
                color="error"
                variant="outlined"
              >
                No
              </Button>
              <Button
                onClick={confirmStatusChange}
                color="success"
                variant="outlined"
              >
                Yes
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <Modal
        open={Boolean(deleteConfirm)}
        onClose={closeDeleteConfirm}
        aria-labelledby="employee-delete-confirm-title"
        aria-describedby="employee-delete-confirm-description"
      >
        <Box sx={confirmModalStyle}>
          <div className="deletemodal">
            <h3
              id="employee-delete-confirm-title"
              className="ftwt__r deletemodal__title"
            >
              Are you sure you want to delete {deleteConfirm?.name}?
            </h3>
            <p
              id="employee-delete-confirm-description"
              className="employeeTable__deleteWarning"
            >
              This action is permanent and cannot be undone.
            </p>
            <TextField
              label="Deletion reason (optional)"
              placeholder="Add a note about why this employee is being deleted"
              multiline
              minRows={3}
              fullWidth
              value={deletionNote}
              onChange={(e) =>
                setDeletionNote(e.target.value.slice(0, DELETION_NOTE_MAX_LENGTH))
              }
              helperText={`${deletionNote.length}/${DELETION_NOTE_MAX_LENGTH}`}
              inputProps={{ maxLength: DELETION_NOTE_MAX_LENGTH }}
              sx={{ mt: 2, mb: 1 }}
            />
            <div className="deletemodal__action">
              <Button
                onClick={closeDeleteConfirm}
                color="inherit"
                variant="outlined"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteEmployee}
                color="error"
                variant="contained"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};
